import {
  Avatar,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  MenuItem,
  Popper,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { GridCloseIcon, GridSearchIcon } from '@mui/x-data-grid';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrowIcon.svg';
import { ReactComponent as DragHandle } from 'assets/icons/drag-handle.svg';
import { ReactComponent as ExclamationIcon } from 'assets/icons/exclamationIcon.svg';
import { ReactComponent as Search } from 'assets/icons/searchGray.svg';
import { ReactComponent as ToolbarFilter } from 'assets/icons/toolbarFilter.svg';
import { ReactComponent as ReturnIcon } from 'assets/icons/returnIcon.svg';
import { ReactComponent as CallIcon } from 'assets/icons/callIcon.svg';
import DateRangeBar from 'assets/icons/dateRangeBar.png';

import PopoverWrapper from 'components/PopoverWrapper';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import {
  ActivityToolbarContainer,
  CallItem,
  CallText,
  DropdownContainer,
  FilterChips,
  HighlightedText,
  InfoTabContainer,
  InfoTitle,
  PaginationNumber,
  PopperContainer,
  SearchContainer,
  SearchHeader,
  SelectFieldContainer,
  StyledSelect,
  StyledSwitch,
  StyledTextField,
  TimelineContainer,
  ToolbarContainer,
  ToolbarElements,
  VerticalDivider,
} from './ui';

const ActivityToolbar: FC = () => {
  const [page, setPage] = useState(0);
  const [activeToolbar, setActiveToolbar] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [dateFilter, setDateFilter] = useState<string>('');
  const [showResults, setShowResults] = useState(false);

  const selectRef = useRef<HTMLDivElement | null>(null); // Corrected ref type

  const iconStyle = { width: '40px', height: '40px' };

  const results = [
    { name: 'John Doe(me)', to: 'Mark Dilver', date: 'Oct 23, 9:05 AM', type: 'Started' },
    { name: 'Mark Dilver', to: 'John Doe(me)', date: 'Oct 11, 11:10 AM', type: 'Received' },
    { name: 'Anne Smith', to: 'Mark Dilver', date: 'Oct 10, 12:10 AM', type: 'Received' },
    { name: 'Mark Dilver', to: 'John Doe(me)', date: 'Oct 10, 11:10 AM', type: 'Received' },
  ];

  const handleChangeRowsPerPage = (event: SelectChangeEvent<unknown>) => {
    setRowsPerPage(parseInt(`${event.target.value}`));
    setPage(0);
  };

  const handleSelectDates = (event: SelectChangeEvent<unknown>) => {
    setDateFilter(`${event.target.value}`);
  };

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleHideShowBar = () => {
    setActiveToolbar(!activeToolbar);
  };

  const handleCancel = () => {
    setShowResults(false);
    setSearchTerm('');
  };

  const handlePopoverOpen = (value: boolean) => {
    setIsPopoverOpen(value);
  };

  const sortOptions = [
    'Alphabetically by Name',
    'Amount (Low to High)',
    'Amount (High to Low)',
    'Close Date',
    'Last Modified Date',
    'Probability (Low to High)',
    'Probability (High to Low)',
  ];

  const marks: { value: number }[] = Array.from({ length: 20 }, (_, i) => ({
    value: i * 5,
  }));

  useEffect(() => {
    // Set the anchor element once the component mounts
    if (selectRef.current) {
      selectRef.current.click(); // Ensure it initializes properly
    }
  }, []);

  return (
    <Rnd
      default={{
        x: 0,
        y: 0,
        width: '40px',
        height: '40px',
      }}
      bounds="parent"
      style={{
        zIndex: 1,
      }}
    >
      <Grid container sx={{ width: 'fit-content' }}>
        <Grid item xs={24} md={24}>
          <ToolbarContainer>
            <ActivityToolbarContainer sx={{ zIndex: 1 }}>
              <ArrowIcon style={iconStyle} onClick={handleHideShowBar} />
              {activeToolbar && (
                <ToolbarElements
                  initial={{ opacity: 0, x: -100 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  <VerticalDivider />
                  <IconButton sx={{ color: '#ffffff' }}>
                    <ExclamationIcon
                      style={{
                        width: '30px',
                        height: '30px',
                      }}
                    />
                  </IconButton>
                  <VerticalDivider />

                  <Divider />

                  {/* Dropdown for items per page */}
                  <StyledSelect value={rowsPerPage} defaultValue={10} onChange={handleChangeRowsPerPage} size="small">
                    <MenuItem value={10}>10 nodes per view</MenuItem>
                    <MenuItem value={20}>20 nodes per view</MenuItem>
                    <MenuItem value={50}>50 nodes per view</MenuItem>
                  </StyledSelect>

                  <Box>
                    <PaginationNumber shape="rounded" count={5} page={page} onChange={handleChange} />
                  </Box>

                  <VerticalDivider />

                  <Divider />

                  {/* Dropdown for items per page */}
                  <StyledSelect value={dateFilter} onChange={handleSelectDates} size="small">
                    <MenuItem value={'week'}>Week</MenuItem>
                    <MenuItem value={'month'}>Month</MenuItem>
                    <MenuItem value={'half-yearly'}>Last 6 months</MenuItem>
                    <MenuItem value={'year'}>Year</MenuItem>
                  </StyledSelect>
                  <VerticalDivider />

                  <Divider />

                  {dateFilter === 'half-yearly' && (
                    <IconButton sx={{ color: '#ffffff', background: '#071731' }}>
                      <ReturnIcon
                        style={{
                          width: '20px',
                          height: '20px',
                        }}
                      />
                      <Typography color={'#9DA1B5'} marginLeft={1}>
                        Back to prev. view{' '}
                      </Typography>
                    </IconButton>
                  )}
                  {/* Filter & Search Icons */}
                  <PopoverWrapper
                    icon={
                      <IconButton sx={{ color: '#ffffff', background: '#071731' }}>
                        <ToolbarFilter
                          style={{
                            width: '25px',
                            height: '25px',
                          }}
                        />
                      </IconButton>
                    }
                  >
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        width: 'fit-content',
                        maxWidth: 360,
                        background: '#071731',
                        color: '#FFF',
                        '& .MuiListItemText-primary': { color: '#FFF', fontSize: '14px' }, // Applies to all text
                      }}
                      aria-labelledby="nested-list-subheader"
                      subheader={
                        <ListSubheader
                          sx={{ background: '#071731', color: '#FFF' }}
                          component="div"
                          id="nested-list-subheader"
                        >
                          Sort by:
                        </ListSubheader>
                      }
                    >
                      {sortOptions.map((option, index) => (
                        <ListItemButton key={index}>
                          <ListItemText primary={option} />
                        </ListItemButton>
                      ))}
                    </List>
                  </PopoverWrapper>
                  <PopoverWrapper
                    icon={
                      <IconButton sx={{ color: '#ffffff', background: '#071731' }}>
                        <Search
                          style={{
                            width: '20px',
                            height: '20px',
                          }}
                        />
                      </IconButton>
                    }
                    handleCancel={handleCancel}
                    handlePopoverOpen={handlePopoverOpen}
                  >
                    <SearchContainer>
                      <SearchHeader>
                        <Typography variant="body1">Quick Search</Typography>
                        <Box display="flex" alignItems="center">
                          <StyledSwitch defaultChecked />
                          <Typography variant="body2">Search within date range</Typography>
                        </Box>
                      </SearchHeader>

                      <SelectFieldContainer ref={selectRef}>
                        <StyledTextField
                          variant="outlined"
                          placeholder="Search..."
                          fullWidth
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setShowResults(e.target.value.length > 0);
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <GridSearchIcon color="disabled" />
                              </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                              <InputAdornment position="end">
                                <GridCloseIcon
                                  style={{ cursor: 'pointer', color: '#A0AEC0' }}
                                  onClick={() => {
                                    setSearchTerm('');
                                    setShowResults(false);
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                        {!showResults && (
                          <PopperContainer className="PopperContainer" top={150} left={130}>
                            Please provide more details for a more accurate search. <br />
                            (e.g., activity title, date, or other specifics)
                          </PopperContainer>
                        )}
                      </SelectFieldContainer>
                    </SearchContainer>
                  </PopoverWrapper>

                  {isPopoverOpen &&
                    (showResults ? (
                      <Popper
                        sx={{ zIndex: 1400 }}
                        open={true}
                        anchorEl={selectRef.current}
                        placement="bottom-start"
                        modifiers={[
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 7], // [Horizontal, Vertical] Adjust based on your needs
                            },
                          },
                        ]}
                      >
                        <DropdownContainer className="DropdownContainer">
                          <Typography variant="body2" color="#9DA1B5" mb={1}>
                            Filter by
                          </Typography>
                          <FilterChips>
                            {['Call', 'Email', 'Event', 'Task', 'Note'].map((filter) => (
                              <Chip key={filter} label={filter} variant="outlined" />
                            ))}
                          </FilterChips>

                          <Typography variant="body2" fontWeight="bold" color="#0057FF" mt={1} mb={1}>
                            Calls
                          </Typography>

                          {results.map((result, index) => (
                            <React.Fragment key={index}>
                              <CallItem>
                                <Avatar sx={{ backgroundColor: '#7D4CDB' }}>
                                  <CallIcon />{' '}
                                </Avatar>
                                <CallText>
                                  <Typography variant="body2">
                                    {result.name} to <HighlightedText>{result.to}</HighlightedText>
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {result.type} on {result.date}
                                  </Typography>
                                </CallText>
                              </CallItem>
                              {index !== results.length - 1 && <Divider />}
                            </React.Fragment>
                          ))}
                        </DropdownContainer>
                      </Popper>
                    ) : null)}

                  <VerticalDivider />

                  <Divider />

                  {/* More Options Icon */}
                  <IconButton sx={{ color: '#ffffff' }}>
                    <DragHandle />
                  </IconButton>
                </ToolbarElements>
              )}
            </ActivityToolbarContainer>
          </ToolbarContainer>
        </Grid>
        {dateFilter === 'half-yearly' && (
          <Grid item xs={24} md={24}>
            <InfoTabContainer>
              <InfoTitle>Acme Inc. - Contacts List (1-10 of 24 contacts)</InfoTitle>
              <TimelineContainer>
                {/* <Typography variant="body2">Jul 23, 2024</Typography>
                <StyledSlider defaultValue={100} step={10} marks={marks} />
                <Typography variant="body2">Dec 23, 2024</Typography> */}

                <img src={DateRangeBar} style={{}} />
              </TimelineContainer>
            </InfoTabContainer>
          </Grid>
        )}
      </Grid>
    </Rnd>
  );
};

export default ActivityToolbar;
